/**
 * Absence Note Query Functions
 * 
 * Direct table queries for absence notes and daily logs.
 * @module fehlzeiten/services/queries/absenceNoteQueries
 */

import { supabase } from '../../../../../lib/supabase';

/**
 * Fetches an absence note by ID.
 */
export async function fetchAbsenceNoteById(
    noteId: string
): Promise<Record<string, unknown> | null> {
    const { data, error } = await supabase
        .from('student_absence_notes' as never)
        .select('id, school_id, student_id, start_date, end_date, absence_status, approval_state, approval_action, approved_at, approved_by, created_at, updated_at, lateness_entries, reason')
        .eq('id' as never, noteId as never)
        .maybeSingle();

    if (error) {
        console.warn('Failed to fetch absence note:', error.message);
        return null;
    }

    return data as Record<string, unknown> | null;
}

/**
 * Fetches daily log for a student on a specific date.
 */
export async function fetchDailyLog(
    studentId: string,
    date: string
): Promise<Record<string, unknown> | null> {
    const { data, error } = await supabase
        .from('student_daily_log' as never)
        .select('id, date, presence_status, check_in_time, check_out_time, notes, created_via, absence_note_id, created_by_profile, expected_checkout_time')
        .eq('student_id' as never, studentId as never)
        .eq('date' as never, date as never)
        .maybeSingle();

    if (error) {
        console.warn('Failed to fetch daily log:', error.message);
        return null;
    }

    return data as Record<string, unknown> | null;
}

/**
 * Fetches absence notes for a student covering a specific date.
 */
export async function fetchAbsenceNotesForDate(
    studentId: string,
    date: string
): Promise<Record<string, unknown>[]> {
    const { data, error } = await supabase
        .from('student_absence_notes' as never)
        .select('id, start_date, end_date, start_time, end_time, absence_status, approval_action, approval_state, reason, created_at, submitted_via, approved_at, created_by, approved_by, updated_at, updated_by')
        .eq('student_id' as never, studentId as never)
        .lte('start_date' as never, date as never)
        .or(`end_date.gte.${date},end_date.is.null`)
        .is('deleted_at' as never, null)
        .order('start_date' as never, { ascending: false });

    if (error) {
        console.warn('Failed to fetch absence notes:', error.message);
        return [];
    }

    return (data || []) as Record<string, unknown>[];
}
